import React, { useMemo, useCallback } from 'react';
import { Helmet } from 'react-helmet';
import { CircleInfoIcon } from '@revfluence/fresh-icons/regular/esm';
import { ChartLineIcon, ChartBarIcon } from '@revfluence/fresh-icons/regular/esm';
import { BadgeDollarIcon, SackDollarIcon, CartShoppingIcon } from '@revfluence/fresh-icons/solid/esm';
import { Tabs, TabsContent } from '@frontend/shadcn/components/ui/tabs';
import { Switch } from '@frontend/shadcn/components/ui/switch';
import { Label } from '@frontend/shadcn/components/ui/label';
import { useAuth } from '@frontend/context/authContext';
import { cn } from '@frontend/shadcn/lib/utils';
import { H3 } from '@frontend/shadcn/components/typography/h3';
import { P } from '@frontend/shadcn/components/typography/p';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@frontend/shadcn/components/ui/tooltip';
import { Skeleton } from '@frontend/shadcn/components/ui/skeleton';
import { graphql, useFragment } from '@frontend/gql/social';

import { Card, CardContent, CardHeader, CardTitle } from '@frontend/shadcn/components/ui/card';
import { useGetCurrentClient } from '@frontend/app/hooks/client/useGetCurrentClient';
import { Period } from '@frontend/gql/social/graphql';
import { SOCIAL_POST_APP_ID } from '@frontend/app/constants/applicationIds';
import { useHistory } from 'react-router-dom';
import { RectangleAdIcon } from '@revfluence/fresh-icons/regular/esm';
import { DateType } from '../Analytics/types';
import { DateRangeForm } from '../Analytics/components/date-range-form';
import {
  useAdsAnalyticsParams,
  useAdsDateDefaults,
  useIntervalRanges,
  useAdsQueryData,
  useUrlInitialization,
  useAdsFilterFormData,
  useAdsDefaultValues,
  type AdsQueryVariables,
} from './hooks';
import TotalSpendCard from './components/ad-highlight-cards/total-spend-card';
import SalesGeneratedCard from './components/ad-highlight-cards/sales-generated-card';
import RoasCard from './components/ad-highlight-cards/roas-card';
import {
  Header,
  HeaderLayout,
  HeaderMainContent,
  HeaderH1,
  HeaderActions,
  HeaderTabs,
  HeaderTabsList,
  HeaderTabsTrigger,
} from '../components/header';
import { Impressions } from './components/ad-highlight-long-card/impressions';
import { Reach } from './components/ad-highlight-long-card/reach';
import { Clicks } from './components/ad-highlight-long-card/clicks';
import AdSpendVsSalesChart from './components/ad-spend-vs-sales-chart';
import RoasOverTimeChart from './components/roas-over-time-chart';
import { Members } from './components/ad-highlight-long-card/members';
import { Posts } from './components/ad-highlight-long-card/posts';
import { Cpc } from './components/ad-highlight-long-card/cpc';
import { Cpm } from './components/ad-highlight-long-card/cpm';
import { Cpp } from './components/ad-highlight-long-card/cpp';
import { Ctr } from './components/ad-highlight-long-card/ctr';
import { CpcRadial } from './components/ad-highlight-long-card/cpc-radial';
import { CpmRadial } from './components/ad-highlight-long-card/cpm-radial';
import { CppRadial } from './components/ad-highlight-long-card/cpp-radial';
import { CtrRadial } from './components/ad-highlight-long-card/ctr-radial';
import ImpressionsChart from './components/impressions-chart';
import ReachChart from './components/reach-chart';
import ClickThroughRateChart from './components/views-per-person-chart';
import { MetricWithRadial } from './components/metric-with-radial';
import { EmptyChart, EmptyHighlightCard, EmptyLongMetric, EmptyMetricWithRadial } from './components/empty-states';
import { isHighlightDataMissing, isMetricDataMissing, isChartDataMissing } from './utils';
import { SocialAlert, AlertButton } from '../components/alert';
import { FilterSheet, RefreshButton, FilterButton } from '../components/shared';
import { useFilterSheet } from '../hooks/shared';
import { getClientCreatedDateForDateRange } from '../utils/shared';
import { AdsFiltersForm, AdsFiltersFormData } from './components/ads-filters-form';
import { useSearchMembers, useMembersByIds, useMergeMembersData } from '../Analytics/hooks';
import TotalAdsCard from './components/ad-highlight-cards/total-ads-card';
// REMOVED: getDefaultSelectedCampaignsAndSets - now handled by useAdsDefaultValues hook

// Analytics Card Components (reusable styled components matching Figma design)
interface AnalyticsCardProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
}

const AnalyticsCard = React.forwardRef<HTMLDivElement, AnalyticsCardProps>(({ className, children, ...props }, ref) => (
  <Card
    ref={ref}
    className={cn('flex flex-col bg-[#ffffff] border border-[#e5e8eb] rounded-2xl', className)}
    {...props}
  >
    {children}
  </Card>
));
AnalyticsCard.displayName = 'AnalyticsCard';

interface AnalyticsCardHeaderProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
}

const AnalyticsCardHeader = React.forwardRef<HTMLDivElement, AnalyticsCardHeaderProps>(
  ({ className, children, ...props }, ref) => (
    <CardHeader ref={ref} className={cn('px-4 py-4 border-b border-[#e5e8eb]', className)} {...props}>
      {children}
    </CardHeader>
  ),
);
AnalyticsCardHeader.displayName = 'AnalyticsCardHeader';

interface AnalyticsCardTitleProps extends React.HTMLAttributes<HTMLHeadingElement> {
  children: React.ReactNode;
}

const AnalyticsCardTitle = React.forwardRef<HTMLParagraphElement, AnalyticsCardTitleProps>(
  ({ className, children, ...props }, ref) => (
    <CardTitle
      ref={ref}
      className={cn(
        "flex items-center gap-2 font-['Inter'] font-medium text-[16px] leading-[28px] text-[#16282d] mb-0",
        className,
      )}
      {...props}
    >
      {children}
    </CardTitle>
  ),
);
AnalyticsCardTitle.displayName = 'AnalyticsCardTitle';

interface AnalyticsCardContentProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
}

const AnalyticsCardContent = React.forwardRef<HTMLDivElement, AnalyticsCardContentProps>(
  ({ className, children, ...props }, ref) => (
    <CardContent ref={ref} className={cn('flex flex-col justify-between h-full px-4 pt-4 pb-6', className)} {...props}>
      {children}
    </CardContent>
  ),
);
AnalyticsCardContent.displayName = 'AnalyticsCardContent';

// Loading components for different sections
const HighlightCardSkeleton = () => (
  <Card className="bg-[#ffffff] border border-[#e5e8eb] rounded-2xl">
    <CardContent className="p-6">
      <div className="space-y-4">
        <Skeleton className="h-5 w-24" />
        <Skeleton className="h-8 w-32" />
        <div className="flex items-center gap-2">
          <Skeleton className="h-4 w-4" />
          <Skeleton className="h-4 w-20" />
        </div>
      </div>
    </CardContent>
  </Card>
);

const LongCardMetricsSkeleton = () => (
  <Card>
    <CardHeader />
    <CardContent>
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-2">
        {Array.from({ length: 5 }).map((_, index) => (
          <div key={index} className="space-y-2 p-4">
            <Skeleton className="h-4 w-16" />
            <Skeleton className="h-6 w-20" />
            <div className="flex items-center gap-1">
              <Skeleton className="h-3 w-3" />
              <Skeleton className="h-3 w-12" />
            </div>
          </div>
        ))}
      </div>
    </CardContent>
  </Card>
);

const ChartCardSkeleton = ({ title }: { title: string }) => (
  <AnalyticsCard>
    <AnalyticsCardHeader>
      <AnalyticsCardTitle>{title}</AnalyticsCardTitle>
    </AnalyticsCardHeader>
    <AnalyticsCardContent>
      <div className="space-y-4">
        <Skeleton className="h-64 w-full" />
        <div className="flex justify-center gap-4">
          <Skeleton className="h-4 w-16" />
          <Skeleton className="h-4 w-16" />
        </div>
      </div>
    </AnalyticsCardContent>
  </AnalyticsCard>
);

const MetricWithRadialSkeleton = ({ title }: { title: string }) => (
  <AnalyticsCard>
    <AnalyticsCardHeader>
      <AnalyticsCardTitle>{title}</AnalyticsCardTitle>
    </AnalyticsCardHeader>
    <AnalyticsCardContent>
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="space-y-2">
            <Skeleton className="h-6 w-16" />
            <div className="flex items-center gap-1">
              <Skeleton className="h-3 w-3" />
              <Skeleton className="h-3 w-12" />
            </div>
          </div>
          <Skeleton className="h-16 w-16 rounded-full" />
        </div>
      </div>
    </AnalyticsCardContent>
  </AnalyticsCard>
);

const DEFAULT_VARIABLES: Omit<AdsQueryVariables, 'clientId'> = {
  dateType: 'last6Months' as DateType,
  startDate: '',
  endDate: '',
  showNonAttributedData: false,
  fromOldData: true,
  // Note: Filter parameters (adAccountIds, etc.) are not yet supported by the backend GraphQL schema
  // These will be added back once the backend MetaInsightsFilterInput type is updated
  adAccountIds: undefined, // Will be set by useUrlInitialization
  adCampaignAndSetIds: undefined, // Will be set by useUrlInitialization
  adObjectiveIds: [],
  memberIds: [],
  compareWithPartnershipAds: false,
};

const DEFAULT_CURRENCY = 'USD';

// Fragment to access currency from currentInterval
const currencyFragment = graphql(`
  fragment SocialAds_CurrencyFragment on MetaSumInsights {
    totalSpend {
      currency
    }
  }
`);

/*
  Component
*/
export const SocialAds = () => {
  const { clientInfo } = useAuth();
  const { client } = useGetCurrentClient();
  const history = useHistory();
  const clientCreatedDate = client?.createdDate;

  // Use custom hooks to organize logic
  const { defaultVariablesWithDates } = useAdsDateDefaults({
    clientCreatedDate,
    defaultVariables: DEFAULT_VARIABLES,
  });

  const {
    queryVariables,
    handleDateRangeSubmit,
    handleShowNonAttributedDataChange,
    updateUrlParams,
    handleFromOldDataChange,
    handleApplyFilters,
  } = useAdsAnalyticsParams({
    clientId: clientInfo?.id,
    defaultVariables: defaultVariablesWithDates,
    clientCreatedDate,
  });

  const { currentIntervalRange, previousIntervalRange } = useIntervalRanges({
    queryVariables,
  });

  const { fetching, refetching, processedData, handleRefetch } = useAdsQueryData({
    clientId: clientInfo?.id,
    currentIntervalRange,
    previousIntervalRange,
    startDate: queryVariables.startDate,
    endDate: queryVariables.endDate,
    fromOldData: queryVariables.fromOldData,
    // Pass filter parameters to enable refetch detection (even though GraphQL query doesn't use them yet)
    // This allows the hook to detect changes and trigger refetch when filters are applied
    adAccountIds: queryVariables.adAccountIds,
    adCampaignAndSetIds: queryVariables.adCampaignAndSetIds,
    adObjectiveIds: queryVariables.adObjectiveIds,
    memberIds: queryVariables.memberIds,
    compareWithPartnershipAds: queryVariables.compareWithPartnershipAds,
  });

  // Destructure processed data for cleaner access
  const {
    metaAdAccounts,
    currentInterval,
    currentIntervalsIntervalInsights,
    currentIntervalsIntervalInsightsNotAspireSourced,
    previousInterval,
    currentIntervalNotAspireSourced,
    previousIntervalNotAspireSourced,
  } = processedData;

  // Determine if we should show loading state
  const isLoading = fetching || refetching;
  const isRefreshing = refetching;

  // Extract currency from API data, fallback to USD if not available
  const currentIntervalCurrencyData = useFragment(currencyFragment, currentInterval);
  const currency = currentIntervalCurrencyData?.totalSpend?.currency || DEFAULT_CURRENCY;

  // Helper function to get precision-based labels
  const getPrecisionLabel = (precision: Period) => {
    switch (precision) {
      case 'DAY':
        return 'Daily';
      case 'QUARTER':
        return 'Quarterly';
      case 'MONTH':
      default:
        return 'Monthly';
    }
  };

  // Alert logic helpers - memoized for performance
  const hasDisconnectedMetaAdAccounts = useMemo(() => {
    return (
      !isLoading &&
      metaAdAccounts &&
      metaAdAccounts.length > 0 &&
      metaAdAccounts.some((account) => account?.status !== 'ACTIVE')
    );
  }, [isLoading, metaAdAccounts]);

  const hasNoMetaAdAccounts = useMemo(() => {
    return !isLoading && (!metaAdAccounts || metaAdAccounts.length === 0);
  }, [isLoading, metaAdAccounts]);

  // Ads filter form data
  const { adAccounts, adCampaigns, adObjectives, isFetchingAdAccounts, isFetchingAdsData } = useAdsFilterFormData(
    clientInfo?.id,
  );

  // Use centralized default values
  const defaultValues = useAdsDefaultValues({
    queryVariables,
    adAccounts,
    adCampaigns,
  });

  // Initialize URL params with default ad accounts (after adAccounts is available)
  useUrlInitialization({
    clientId: clientInfo?.id,
    clientCreatedDate,
    defaultDateType: DEFAULT_VARIABLES.dateType,
    defaultShowNonAttributedData: DEFAULT_VARIABLES.showNonAttributedData,
    defaultAdAccountIds: defaultValues.adAccountIds,
    defaultAdCampaignAndSetIds: defaultValues.adCampaignAndSetIds,
    isFetchingAdAccounts,
    updateUrlParams,
  });

  // Member search functionality
  const [searchMemberTerm, setMemberSearchTerm] = React.useState<string>('');

  // Query for search term
  const { data: searchData, loading: searchLoading, error: searchError } = useSearchMembers(searchMemberTerm);

  // Use memberIds from URL params for the member data
  const memberIdsFromQuery = useMemo(
    () => (queryVariables?.memberIds || []).map((id) => parseInt(id, 10)).filter((id) => !isNaN(id)),
    [queryVariables?.memberIds],
  );
  const { data: memberIdsData, loading: memberIdsLoading, error: memberIdsError } = useMembersByIds(memberIdsFromQuery);

  // Combine and deduplicate the results
  const {
    data: { members = [] },
    loading: isFetchingMembers,
  } = useMergeMembersData(searchData, searchLoading, searchError, memberIdsData, memberIdsLoading, memberIdsError);

  // Convert members to match the updated Member type with string id
  const mappedMembers = useMemo(
    () =>
      members.map((member) => ({
        ...member,
        id: String(member.id),
        name: member.name ?? undefined,
        email: member.email ?? undefined,
        profilePicture: member.profilePicture ?? undefined,
      })),
    [members],
  );

  // Alert handlers
  const handleConnectInstagram = () => {
    // Handle connect Instagram account action
    history.push(`/settings/${SOCIAL_POST_APP_ID}`);
  };

  const handleReconnectAccount = () => {
    // Handle reconnect account action
    history.push(`/settings/${SOCIAL_POST_APP_ID}`);
  };

  // Filter sheet state management
  const {
    isOpen: isFiltersOpen,
    handleOpen: handleOpenFilters,
    handleCancel: handleCancelFilters,
  } = useFilterSheet({
    onApplyFilters: () => {}, // Will be overridden by handleFormSubmit
  });

  // REMOVED: Default values logic now centralized in useAdsDefaultValues hook

  // Filter functionality with form support - SIMPLIFIED
  const handleFormSubmit = useCallback(
    (formData: AdsFiltersFormData) => {
      handleApplyFilters(formData); // ✅ Direct pass-through
      handleCancelFilters();
    },
    [handleApplyFilters, handleCancelFilters],
  );

  return (
    <TooltipProvider>
      <Helmet>
        <title>Ads Dashboard - Aspire</title>
      </Helmet>
      <Tabs defaultValue="instagram">
        <Header>
          <HeaderLayout>
            <HeaderMainContent>
              <HeaderH1>Ads Dashboard</HeaderH1>
            </HeaderMainContent>
            <HeaderActions>
              <RefreshButton
                isRefreshing={isRefreshing}
                onClick={handleRefetch}
                disabled={false} // Always enabled since we only have one tab
                dataAttribute="refresh-ads-data"
                testId="refresh-ads-button"
              />
              <DateRangeForm
                onApply={handleDateRangeSubmit}
                defaultValues={{
                  dateType: queryVariables.dateType,
                  dateRange:
                    queryVariables.startDate && queryVariables.endDate
                      ? {
                          from: queryVariables.startDate,
                          to: queryVariables.endDate,
                        }
                      : undefined,
                }}
                minDate={getClientCreatedDateForDateRange(clientCreatedDate)}
                aria-label="Date range selector"
              />
              <FilterButton onClick={handleOpenFilters} isExpanded={isFiltersOpen} dataAttribute="open-ads-filters" />
            </HeaderActions>
          </HeaderLayout>
          <HeaderTabs>
            <HeaderTabsList>
              <HeaderTabsTrigger value="instagram">Instagram Ads</HeaderTabsTrigger>
            </HeaderTabsList>
          </HeaderTabs>
        </Header>
        <div className="">
          <TabsContent className="flex flex-col gap-6 pt-4 pb-4" value="instagram">
            <div className="px-6">
              <div className="flex flex-col gap-3 items-end">
                <div className="flex items-center gap-2">
                  <Switch
                    id="show-non-attributed"
                    checked={queryVariables.showNonAttributedData}
                    onCheckedChange={handleShowNonAttributedDataChange}
                    className="data-[state=checked]:bg-primary"
                    disabled={isLoading}
                  />
                  <Label
                    htmlFor="show-non-attributed"
                    className="text-sm font-normal text-[#16282d] cursor-pointer whitespace-nowrap"
                  >
                    Show data not attributed to Aspire
                  </Label>
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    id="from-old-data"
                    checked={queryVariables.fromOldData}
                    onCheckedChange={handleFromOldDataChange}
                    className="data-[state=checked]:bg-primary"
                    disabled={isLoading}
                  />
                  <Label
                    htmlFor="from-old-data"
                    className="text-sm font-normal text-[#16282d] cursor-pointer whitespace-nowrap"
                  >
                    Use old data source
                  </Label>
                </div>
              </div>
            </div>

            {/* Alerts section - only show if there are alerts to display */}
            {(hasNoMetaAdAccounts || hasDisconnectedMetaAdAccounts) && (
              <div className="px-6 flex flex-col gap-4">
                {/* Show connect alert only if no ad accounts */}
                {hasNoMetaAdAccounts && (
                  <SocialAlert
                    type="info"
                    title="Connect your Instagram Ad Account"
                    description="Analyze how well your ads are performing by connecting your Instagram Ad Account to Aspire."
                    button={<AlertButton onClick={handleConnectInstagram}>Connect Account</AlertButton>}
                  />
                )}

                {/* Show reconnect alert only if there are disconnected accounts */}
                {hasDisconnectedMetaAdAccounts && (
                  <SocialAlert
                    type="error"
                    title="Ad account has been disconnected"
                    description="We have detected an issue with your connected Instagram Ad account. To ensure seamless operation, please reconnect your account."
                    button={<AlertButton onClick={handleReconnectAccount}>Reconnect Account</AlertButton>}
                  />
                )}
              </div>
            )}
            <div className="px-6 mb-0">
              <H3>Ads Highlights</H3>
              <P>
                Key metrics define your success, helping you quickly assess what's working and where to dive deeper. Use
                it to identify how your content is generated through Aspire performing as Ads.
              </P>
            </div>

            {/* Ads Highlights Section */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 px-6">
              {isLoading ? (
                <>
                  <HighlightCardSkeleton />
                  <HighlightCardSkeleton />
                  <HighlightCardSkeleton />
                  <HighlightCardSkeleton />
                </>
              ) : (
                <>
                  {isHighlightDataMissing(currentInterval, 'totalAds') ? (
                    <EmptyHighlightCard
                      title="Total Ads"
                      iconComponent={<RectangleAdIcon className="mr-2 text-[1.5rem]" aria-hidden="true" />}
                      backgroundColor="bg-[#1E293B]"
                      valueType="number"
                      showNonAttributedData={queryVariables.showNonAttributedData}
                    />
                  ) : (
                    <TotalAdsCard
                      currentInterval={currentInterval}
                      previousInterval={previousInterval}
                      currentIntervalNotAspireSourced={
                        queryVariables.showNonAttributedData ? currentIntervalNotAspireSourced : null
                      }
                      previousIntervalNotAspireSourced={
                        queryVariables.showNonAttributedData ? previousIntervalNotAspireSourced : null
                      }
                      showNonAttributedData={queryVariables.showNonAttributedData}
                      startDate={queryVariables.startDate}
                      endDate={queryVariables.endDate}
                      dateType={queryVariables.dateType}
                    />
                  )}
                  {isHighlightDataMissing(currentInterval, 'totalSpend') ? (
                    <EmptyHighlightCard
                      title="Ads Spend"
                      iconComponent={<BadgeDollarIcon className="mr-2 text-[1.5rem]" aria-hidden="true" />}
                      backgroundColor="bg-[#003F5B]"
                      valueType="currency"
                      showNonAttributedData={queryVariables.showNonAttributedData}
                    />
                  ) : (
                    <TotalSpendCard
                      currentInterval={currentInterval}
                      previousInterval={previousInterval}
                      currentIntervalNotAspireSourced={
                        queryVariables.showNonAttributedData ? currentIntervalNotAspireSourced : null
                      }
                      previousIntervalNotAspireSourced={
                        queryVariables.showNonAttributedData ? previousIntervalNotAspireSourced : null
                      }
                      showNonAttributedData={queryVariables.showNonAttributedData}
                      startDate={queryVariables.startDate}
                      endDate={queryVariables.endDate}
                      dateType={queryVariables.dateType}
                    />
                  )}
                  {isHighlightDataMissing(currentInterval, 'totalPurchase') ? (
                    <EmptyHighlightCard
                      title="Sales Generated from Ads"
                      iconComponent={<CartShoppingIcon className="mr-2 text-[1.5rem]" aria-hidden="true" />}
                      backgroundColor="bg-[#2F7772]"
                      valueType="currency"
                      showNonAttributedData={queryVariables.showNonAttributedData}
                    />
                  ) : (
                    <SalesGeneratedCard
                      currentInterval={currentInterval}
                      previousInterval={previousInterval}
                      currentIntervalNotAspireSourced={
                        queryVariables.showNonAttributedData ? currentIntervalNotAspireSourced : null
                      }
                      previousIntervalNotAspireSourced={
                        queryVariables.showNonAttributedData ? previousIntervalNotAspireSourced : null
                      }
                      showNonAttributedData={queryVariables.showNonAttributedData}
                      startDate={queryVariables.startDate}
                      endDate={queryVariables.endDate}
                      dateType={queryVariables.dateType}
                    />
                  )}
                  {isMetricDataMissing(currentInterval, 'totalRoas') ? (
                    <EmptyHighlightCard
                      title="ROAS"
                      iconComponent={<SackDollarIcon className="mr-2 text-[1.5rem]" aria-hidden="true" />}
                      backgroundColor="bg-[#58508D]"
                      valueType="percentage"
                      showNonAttributedData={queryVariables.showNonAttributedData}
                    />
                  ) : (
                    <RoasCard
                      currentInterval={currentInterval}
                      previousInterval={previousInterval}
                      currentIntervalNotAspireSourced={
                        queryVariables.showNonAttributedData ? currentIntervalNotAspireSourced : null
                      }
                      previousIntervalNotAspireSourced={
                        queryVariables.showNonAttributedData ? previousIntervalNotAspireSourced : null
                      }
                      showNonAttributedData={queryVariables.showNonAttributedData}
                      startDate={queryVariables.startDate}
                      endDate={queryVariables.endDate}
                      dateType={queryVariables.dateType}
                    />
                  )}
                </>
              )}

              {/* Long metrics card */}
              <div className="col-span-1 lg:col-span-4">
                {isLoading ? (
                  <LongCardMetricsSkeleton />
                ) : (
                  <Card>
                    <CardHeader />
                    <CardContent>
                      <div className="grid grid-cols-2 lg:grid-cols-5 gap-2">
                        {isMetricDataMissing(currentInterval, 'totalClicks') ? (
                          <EmptyLongMetric label="Clicks" />
                        ) : (
                          <Clicks currentInterval={currentInterval} previousInterval={previousInterval} />
                        )}
                        {isMetricDataMissing(currentInterval, 'totalReach') ? (
                          <EmptyLongMetric label="Reach" />
                        ) : (
                          <Reach currentInterval={currentInterval} previousInterval={previousInterval} />
                        )}
                        {isMetricDataMissing(currentInterval, 'totalImpressions') ? (
                          <EmptyLongMetric label="Impressions" />
                        ) : (
                          <Impressions currentInterval={currentInterval} previousInterval={previousInterval} />
                        )}
                        {isMetricDataMissing(currentInterval, 'totalCreators') ? (
                          <EmptyLongMetric label="Members" />
                        ) : (
                          <Members currentInterval={currentInterval} previousInterval={previousInterval} />
                        )}
                        {isMetricDataMissing(currentInterval, 'totalPosts') ? (
                          <EmptyLongMetric label="Posts" />
                        ) : (
                          <Posts currentInterval={currentInterval} previousInterval={previousInterval} />
                        )}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            </div>

            {/* Ads Spend Overview Section */}
            <div className="px-6">
              <H3>Ads Spend Overview</H3>
              <P>
                Ensure that every dollar is working toward your Ads' goals. Examining how much you spend over time and
                the revenue attributable to ads through content generated through Aspire.
              </P>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-6 gap-4 px-6">
              {isLoading ? (
                <>
                  <div className="col-span-1 lg:col-span-4">
                    <ChartCardSkeleton title="Ad Spend vs. Sales" />
                  </div>
                  <div className="col-span-1 lg:col-span-2">
                    <ChartCardSkeleton title="ROAS Over Time" />
                  </div>
                </>
              ) : (
                <>
                  <AnalyticsCard className="col-span-1 lg:col-span-4">
                    <AnalyticsCardHeader>
                      <AnalyticsCardTitle>
                        Ad Spend vs. Sales
                        <Tooltip>
                          <TooltipTrigger className="flex items-center">
                            <CircleInfoIcon className="w-4 h-4 text-[#cbd1d7] hover:text-[#5d6974] transition-colors" />
                          </TooltipTrigger>
                          <TooltipContent className="max-w-[250px]">
                            <P className="text-sm">
                              {getPrecisionLabel(currentIntervalRange.precision)} Ad Spend and Sales trends, split into
                              Aspire-attributed and non-attributed portions for comparison.
                            </P>
                          </TooltipContent>
                        </Tooltip>
                      </AnalyticsCardTitle>
                    </AnalyticsCardHeader>
                    <AnalyticsCardContent>
                      {isChartDataMissing(currentIntervalsIntervalInsights) ? (
                        <EmptyChart
                          title="Ad Spend or Sales Data Unavailable"
                          message="Try adjusting your filters or check back once your campaigns has gathered more results."
                          iconComponent={<ChartLineIcon className="w-12 h-12" />}
                        />
                      ) : (
                        <AdSpendVsSalesChart
                          currentIntervalsIntervalInsights={currentIntervalsIntervalInsights}
                          currentIntervalsIntervalInsightsNotAspireSourced={
                            currentIntervalsIntervalInsightsNotAspireSourced
                          }
                          showNonAttributedData={queryVariables.showNonAttributedData}
                          precision={currentIntervalRange.precision}
                          currency={currency}
                        />
                      )}
                    </AnalyticsCardContent>
                  </AnalyticsCard>
                  <AnalyticsCard className="col-span-1 lg:col-span-2">
                    <AnalyticsCardHeader>
                      <AnalyticsCardTitle>
                        ROAS Over Time
                        <Tooltip>
                          <TooltipTrigger className="flex items-center">
                            <CircleInfoIcon className="w-4 h-4 text-[#cbd1d7] hover:text-[#5d6974] transition-colors" />
                          </TooltipTrigger>
                          <TooltipContent className="max-w-[250px]">
                            <P className="text-sm">
                              Total return on ad spend (ROAS) from purchases of items shared between Brand and Retailer.
                              This number is based on information received from one or more retailers' connected
                              Facebook Business Tools. You can see the amount's breakdown is attributed to Aspire vs not
                              attributed to Aspire.
                            </P>
                          </TooltipContent>
                        </Tooltip>
                      </AnalyticsCardTitle>
                    </AnalyticsCardHeader>
                    <AnalyticsCardContent>
                      {isChartDataMissing(currentIntervalsIntervalInsights) ? (
                        <EmptyChart
                          title="ROAS Data Unavailable"
                          message="Try adjusting your filters or check back once your campaigns has gathered more results."
                          iconComponent={<ChartLineIcon className="w-12 h-12" />}
                        />
                      ) : (
                        <RoasOverTimeChart
                          currentIntervalsMetaInsights={currentIntervalsIntervalInsights}
                          currentIntervalsMetaInsightsNotAspireSourced={
                            currentIntervalsIntervalInsightsNotAspireSourced
                          }
                          showNonAttributedData={queryVariables.showNonAttributedData}
                          precision={currentIntervalRange.precision}
                        />
                      )}
                    </AnalyticsCardContent>
                  </AnalyticsCard>
                </>
              )}
            </div>

            {/* Ads Awareness Section */}
            <div className="px-6">
              <H3>Ads Awareness</H3>
              <P>
                It shows how far your message is spreading and who is seeing it. By analyzing these metrics, you can
                understand the scale of your brand's visibility and fine-tune targeting to ensure your message reaches
                the right audience in the right volume.
              </P>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 px-6">
              {isLoading ? (
                <>
                  <ChartCardSkeleton title="Impressions" />
                  <ChartCardSkeleton title="Reach" />
                  <ChartCardSkeleton title="Views per person" />
                </>
              ) : (
                <>
                  <AnalyticsCard className="col-span-1">
                    <AnalyticsCardHeader>
                      <AnalyticsCardTitle>Impressions</AnalyticsCardTitle>
                    </AnalyticsCardHeader>
                    <AnalyticsCardContent>
                      {isChartDataMissing(currentIntervalsIntervalInsights) ? (
                        <EmptyChart
                          title="Impressions Data Unavailable"
                          message="Try adjusting your filters or check back once your campaigns has gathered more results."
                          iconComponent={<ChartBarIcon className="w-12 h-12" />}
                        />
                      ) : (
                        <ImpressionsChart
                          currentIntervalsIntervalInsights={currentIntervalsIntervalInsights}
                          currentIntervalsIntervalInsightsNotAspireSourced={
                            currentIntervalsIntervalInsightsNotAspireSourced
                          }
                          showNonAttributedData={queryVariables.showNonAttributedData}
                          precision={currentIntervalRange.precision}
                        />
                      )}
                    </AnalyticsCardContent>
                  </AnalyticsCard>

                  <AnalyticsCard className="col-span-1">
                    <AnalyticsCardHeader>
                      <AnalyticsCardTitle>Reach</AnalyticsCardTitle>
                    </AnalyticsCardHeader>
                    <AnalyticsCardContent>
                      {isChartDataMissing(currentIntervalsIntervalInsights) ? (
                        <EmptyChart
                          title="Reach Data Unavailable"
                          message="Try adjusting your filters or check back once your campaigns has gathered more results."
                          iconComponent={<ChartBarIcon className="w-12 h-12" />}
                        />
                      ) : (
                        <ReachChart
                          currentIntervalsIntervalInsights={currentIntervalsIntervalInsights}
                          currentIntervalsIntervalInsightsNotAspireSourced={
                            currentIntervalsIntervalInsightsNotAspireSourced
                          }
                          showNonAttributedData={queryVariables.showNonAttributedData}
                          precision={currentIntervalRange.precision}
                        />
                      )}
                    </AnalyticsCardContent>
                  </AnalyticsCard>

                  <AnalyticsCard className="col-span-1">
                    <AnalyticsCardHeader>
                      <AnalyticsCardTitle>Views per person</AnalyticsCardTitle>
                    </AnalyticsCardHeader>
                    <AnalyticsCardContent>
                      {isChartDataMissing(currentIntervalsIntervalInsights) ? (
                        <EmptyChart
                          title="Views Data Unavailable"
                          message="Try adjusting your filters or check back once your campaigns has gathered more results."
                          iconComponent={<ChartBarIcon className="w-12 h-12" />}
                        />
                      ) : (
                        <ClickThroughRateChart
                          currentIntervalsIntervalInsights={currentIntervalsIntervalInsights}
                          currentIntervalsIntervalInsightsNotAspireSourced={
                            currentIntervalsIntervalInsightsNotAspireSourced
                          }
                          showNonAttributedData={queryVariables.showNonAttributedData}
                          precision={currentIntervalRange.precision}
                        />
                      )}
                    </AnalyticsCardContent>
                  </AnalyticsCard>
                </>
              )}
            </div>

            {/* Metric Cards with Radials Section */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 px-6">
              {isLoading ? (
                <>
                  <MetricWithRadialSkeleton title="CPC" />
                  <MetricWithRadialSkeleton title="CPM" />
                  <MetricWithRadialSkeleton title="CPP" />
                  <MetricWithRadialSkeleton title="CTR" />
                </>
              ) : (
                <>
                  <AnalyticsCard>
                    <AnalyticsCardHeader>
                      <AnalyticsCardTitle>CPC</AnalyticsCardTitle>
                    </AnalyticsCardHeader>
                    <AnalyticsCardContent>
                      {isMetricDataMissing(currentInterval, 'totalCpc') ? (
                        <EmptyMetricWithRadial
                          valueType="currency"
                          currency={currency}
                          showNonAttributedData={queryVariables.showNonAttributedData}
                        />
                      ) : (
                        <MetricWithRadial
                          metricComponent={
                            <Cpc
                              currentInterval={currentInterval}
                              previousInterval={previousInterval}
                              currentIntervalNotAspireSourced={
                                queryVariables.showNonAttributedData ? currentIntervalNotAspireSourced : null
                              }
                              previousIntervalNotAspireSourced={
                                queryVariables.showNonAttributedData ? previousIntervalNotAspireSourced : null
                              }
                              showNonAttributedData={queryVariables.showNonAttributedData}
                              currency={currency}
                            />
                          }
                          radialComponent={
                            <CpcRadial
                              currentInterval={currentInterval}
                              currentIntervalNotAspireSourced={currentIntervalNotAspireSourced}
                              showNonAttributedData={queryVariables.showNonAttributedData}
                              currency={currency}
                            />
                          }
                          showNonAttributedData={queryVariables.showNonAttributedData}
                        />
                      )}
                    </AnalyticsCardContent>
                  </AnalyticsCard>
                  <AnalyticsCard>
                    <AnalyticsCardHeader>
                      <AnalyticsCardTitle>CPM</AnalyticsCardTitle>
                    </AnalyticsCardHeader>
                    <AnalyticsCardContent>
                      {isMetricDataMissing(currentInterval, 'totalCpm') ? (
                        <EmptyMetricWithRadial
                          valueType="currency"
                          currency={currency}
                          showNonAttributedData={queryVariables.showNonAttributedData}
                        />
                      ) : (
                        <MetricWithRadial
                          metricComponent={
                            <Cpm
                              currentInterval={currentInterval}
                              previousInterval={previousInterval}
                              currentIntervalNotAspireSourced={
                                queryVariables.showNonAttributedData ? currentIntervalNotAspireSourced : null
                              }
                              previousIntervalNotAspireSourced={
                                queryVariables.showNonAttributedData ? previousIntervalNotAspireSourced : null
                              }
                              showNonAttributedData={queryVariables.showNonAttributedData}
                              currency={currency}
                            />
                          }
                          radialComponent={
                            <CpmRadial
                              currentInterval={currentInterval}
                              currentIntervalNotAspireSourced={currentIntervalNotAspireSourced}
                              showNonAttributedData={queryVariables.showNonAttributedData}
                              currency={currency}
                            />
                          }
                          showNonAttributedData={queryVariables.showNonAttributedData}
                        />
                      )}
                    </AnalyticsCardContent>
                  </AnalyticsCard>
                  <AnalyticsCard>
                    <AnalyticsCardHeader>
                      <AnalyticsCardTitle>CPP</AnalyticsCardTitle>
                    </AnalyticsCardHeader>
                    <AnalyticsCardContent>
                      {isMetricDataMissing(currentInterval, 'totalCpp') ? (
                        <EmptyMetricWithRadial
                          valueType="currency"
                          currency={currency}
                          showNonAttributedData={queryVariables.showNonAttributedData}
                        />
                      ) : (
                        <MetricWithRadial
                          metricComponent={
                            <Cpp
                              currentInterval={currentInterval}
                              previousInterval={previousInterval}
                              currentIntervalNotAspireSourced={
                                queryVariables.showNonAttributedData ? currentIntervalNotAspireSourced : null
                              }
                              previousIntervalNotAspireSourced={
                                queryVariables.showNonAttributedData ? previousIntervalNotAspireSourced : null
                              }
                              showNonAttributedData={queryVariables.showNonAttributedData}
                              currency={currency}
                            />
                          }
                          radialComponent={
                            <CppRadial
                              currentInterval={currentInterval}
                              currentIntervalNotAspireSourced={currentIntervalNotAspireSourced}
                              showNonAttributedData={queryVariables.showNonAttributedData}
                              currency={currency}
                            />
                          }
                          showNonAttributedData={queryVariables.showNonAttributedData}
                        />
                      )}
                    </AnalyticsCardContent>
                  </AnalyticsCard>
                  <AnalyticsCard>
                    <AnalyticsCardHeader>
                      <AnalyticsCardTitle>CTR</AnalyticsCardTitle>
                    </AnalyticsCardHeader>
                    <AnalyticsCardContent>
                      {isMetricDataMissing(currentInterval, 'totalClicks') ||
                      isMetricDataMissing(currentInterval, 'totalImpressions') ? (
                        <EmptyMetricWithRadial
                          valueType="percentage"
                          showNonAttributedData={queryVariables.showNonAttributedData}
                        />
                      ) : (
                        <MetricWithRadial
                          metricComponent={
                            <Ctr
                              currentInterval={currentInterval}
                              previousInterval={previousInterval}
                              currentIntervalNotAspireSourced={
                                queryVariables.showNonAttributedData ? currentIntervalNotAspireSourced : null
                              }
                              previousIntervalNotAspireSourced={
                                queryVariables.showNonAttributedData ? previousIntervalNotAspireSourced : null
                              }
                              showNonAttributedData={queryVariables.showNonAttributedData}
                            />
                          }
                          radialComponent={
                            <CtrRadial
                              currentInterval={currentInterval}
                              currentIntervalNotAspireSourced={currentIntervalNotAspireSourced}
                              showNonAttributedData={queryVariables.showNonAttributedData}
                            />
                          }
                          showNonAttributedData={queryVariables.showNonAttributedData}
                        />
                      )}
                    </AnalyticsCardContent>
                  </AnalyticsCard>
                </>
              )}
            </div>
          </TabsContent>
        </div>
      </Tabs>

      {/* Filter Sheet using shared component */}
      <FilterSheet
        isOpen={isFiltersOpen}
        onOpenChange={(open) => {
          if (open) {
            handleOpenFilters();
          } else {
            handleCancelFilters();
          }
        }}
        title="Ads Filters"
        onCancel={handleCancelFilters}
        onApply={() => {}} // No-op since form handles its own submission
        cancelDataAttribute="cancel-ads-filters"
        applyDataAttribute="apply-ads-filters"
      >
        <AdsFiltersForm
          adAccounts={adAccounts}
          adCampaigns={adCampaigns}
          adObjectives={adObjectives}
          isFetchingAdAccounts={isFetchingAdAccounts}
          isFetchingAdsData={isFetchingAdsData}
          members={mappedMembers}
          setMemberSearchTerm={setMemberSearchTerm}
          isFetchingMembers={isFetchingMembers ?? false}
          defaultValues={defaultValues}
          onSubmit={handleFormSubmit}
          onCancel={handleCancelFilters}
        />
      </FilterSheet>
    </TooltipProvider>
  );
};
